import { format, isSameDay, Locale } from 'date-fns'

// #77 — the announcement banner: an update to come or a maintenance, written
// by Yohan in the console, shown to every signed-in account until its planned
// end, unless this person closed it (a new announcement shows again).

interface AnnouncementLike {
  id: string
  endsAt: string
}

export function announcementToShow<A extends AnnouncementLike>(
  announcement: A | undefined,
  dismissedId: string | undefined,
  now: Date
): A | undefined {
  if (!announcement) return undefined
  if (announcement.id === dismissedId) return undefined
  if (new Date(announcement.endsAt) <= now) return undefined
  return announcement
}

// « dim. 5 oct., 22:00 → 23:30 », or the second date in full when the window
// spans several days.
export function formatAnnouncementDates(
  startsAt: string,
  endsAt: string,
  locale: Locale
): string {
  const start = new Date(startsAt)
  const end = new Date(endsAt)
  const day = 'EEE d MMM, HH:mm'
  return `${format(start, day, { locale })} → ${format(
    end,
    isSameDay(start, end) ? 'HH:mm' : day,
    { locale }
  )}`
}

// In the console: « current » while its planned end has not passed (it is
// shown from publication), « past » after.
export function isAnnouncementCurrent(
  announcement: AnnouncementLike,
  now: Date
): boolean {
  return new Date(announcement.endsAt) > now
}

// What closing an announcement writes, when a new one replaces it: its end
// becomes now. One whose start is still to come also starts now, since the
// database refuses an end before the start.
export function closingValues(
  announcement: { startsAt: string },
  now: Date
): { startsAt?: string; endsAt: string } {
  const end = now.toISOString()
  if (new Date(announcement.startsAt) < now) return { endsAt: end }
  return { startsAt: new Date(now.getTime() - 1000).toISOString(), endsAt: end }
}

// A link is optional; when given it must be a web address
export function isValidAnnouncementUrl(url: string): boolean {
  if (!url.trim()) return true
  try {
    return ['http:', 'https:'].includes(new URL(url.trim()).protocol)
  } catch {
    return false
  }
}
